import { useTranslation } from 'react-i18next';
import { alpha, Box, styled } from '@mui/material';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import InfoList from '@/components/Approval/@components/InfoList';
import {
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import { CurrencyLabel } from '@/components/Common/CurrencyFlag';
import {
  currencyTotalsFromAttributes,
  EXPENSE_VOUCHER_CONFIRM_ACCOUNT_MATCH_TEXT,
  EXPENSE_VOUCHER_CONFIRM_PAY_BASIS_TEXT,
  formatVoucherNumber,
  isExpenseVoucherAttributes,
  payMethodLabel,
  toCurrency,
  vendorStatusLabel,
} from '@/utils/approval/expenseVoucher';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

const CONFIRM_TEXTS = [
  ['confirmAccountMatch', EXPENSE_VOUCHER_CONFIRM_ACCOUNT_MATCH_TEXT],
  ['confirmPayBasis', EXPENSE_VOUCHER_CONFIRM_PAY_BASIS_TEXT],
] as const;

const Summary = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '4px 12px',
  padding: '20px 24px',
  borderRadius: 8,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  '& .label': {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--dk-text, #131313)',
  },
  '& .value': {
    fontSize: 20,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  '& .value i': {
    fontStyle: 'normal',
    fontSize: 13,
    fontWeight: 600,
    marginLeft: 4,
    color: 'var(--dk-text, #131313)',
  },
  '& .sub': {
    marginLeft: 'auto',
    fontSize: 16,
    color: 'var(--dk-text, #131313)',
  },
}));

// 레거시 info-grid — 2열, 좁은 화면 1열
const TwoColumnInfo = styled(Box)(({ theme }) => ({
  '& > ul': {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 24,
    [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
  },
  '& > ul > li:first-of-type': { borderTop: 'none' },
}));

const StatusBadge = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '18px',
  backgroundColor: 'var(--dk-bg-secondary, #f1f3f5)',
  '&.warn': {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },
}));

const ConfirmList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  '& li': { display: 'flex', gap: 8, fontSize: 14, lineHeight: '20px' },
  '& .mark': { flex: '0 0 16px', textAlign: 'center' },
  '& li.checked .mark': { color: 'var(--dk-primary, #0066cc)' },
});

const text = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';

const dash = (value: string) => value || '-';

/** 111 지출결의서 — 저장값으로 레거시 조회 화면을 그림. 개편 이전 문서(attributes 없음)는 본문만 */
const ExpenseVoucherSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const saved = data.attributes as Record<string, unknown> | null | undefined;
  if (!isExpenseVoucherAttributes(saved)) return null;

  const rows = (Array.isArray(saved.items) ? saved.items : []).filter(
    (r): r is Record<string, unknown> => !!r && typeof r === 'object',
  );
  const totals = currencyTotalsFromAttributes(saved.currencyTotals);
  const payMethod = payMethodLabel(saved.payMethod);
  const payRequestDate = text(saved.payRequestDate);
  const vendorStatus = vendorStatusLabel(saved.vendorStatus);
  const summarySub = [payMethod && t(payMethod), payRequestDate]
    .filter(Boolean)
    .join(' · ');

  return (
    <ContentSlotRoot>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${APPROVAL_SECTION_GAP_LG}px`,
        }}
      >
        <Summary>
          <span className="label">{t('총 지급액')}</span>
          {totals.map((total) => (
            <span className="value" key={total.currency}>
              {total.total}
              <i>{total.currency}</i>
            </span>
          ))}
          {summarySub && <span className="sub">{summarySub}</span>}
        </Summary>

        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>
            {`1. ${t('지급 내역')}`}
          </InfoSectionTitle>
          <ApprovalFormTable>
            <colgroup>
              <col style={{ width: 46 }} />
              <col style={{ width: '13%' }} />
              <col />
              <col style={{ width: '8%' }} />
              <col style={{ width: 96 }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>NO.</th>
                <th>{t('구분')}</th>
                <th>{t('적요')}</th>
                <th>{t('수량')}</th>
                <th>{t('통화')}</th>
                <th>{t('금액')}</th>
                <th>{t('비고')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="align-center">
                    {t('데이터가 없습니다.')}
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${index}-${text(row.seq)}`}>
                    <td className="align-center">{index + 1}</td>
                    <td>{dash(text(row.category))}</td>
                    <td>{dash(text(row.summary))}</td>
                    <td className="align-right">
                      {dash(formatVoucherNumber(text(row.quantity)))}
                    </td>
                    <td className="align-center">
                      <CurrencyLabel currency={toCurrency(row.currency)} />
                    </td>
                    <td className="align-right">
                      {dash(formatVoucherNumber(text(row.amount)))}
                    </td>
                    <td>{dash(text(row.remark))}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              {totals.map((total, index) => (
                <tr key={total.currency}>
                  {index === 0 && (
                    <td colSpan={4} rowSpan={totals.length}>
                      <strong>{t('총 합계')}</strong>
                    </td>
                  )}
                  <td className="align-center">
                    <CurrencyLabel currency={total.currency} />
                  </td>
                  <td className="align-right">
                    <strong>{total.total}</strong>
                  </td>
                  {index === 0 && <td rowSpan={totals.length} />}
                </tr>
              ))}
            </tfoot>
          </ApprovalFormTable>
        </Box>

        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>
            {`2. ${t('지급 정보')}`}
          </InfoSectionTitle>
          <TwoColumnInfo>
            <InfoList
              labelWidth={110}
              rows={[
                {
                  label: t('지급 방법'),
                  value: dash(payMethod && t(payMethod)),
                },
                { label: t('지급 요청일자'), value: dash(payRequestDate) },
              ]}
            />
          </TwoColumnInfo>
        </Box>

        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>
            {`3. ${t('거래처 정보')}`}
          </InfoSectionTitle>
          <TwoColumnInfo>
            <InfoList
              labelWidth={110}
              rows={[
                {
                  label: t('BP 거래처번호'),
                  value: dash(text(saved.bpVendorNumber)),
                },
                { label: t('거래처명'), value: dash(text(saved.vendorName)) },
                {
                  label: t('거래처 상태'),
                  value: vendorStatus ? (
                    <StatusBadge
                      className={saved.vendorStatus !== 'normal' ? 'warn' : ''}
                    >
                      {t(vendorStatus)}
                    </StatusBadge>
                  ) : (
                    '-'
                  ),
                },
                { label: t('은행명'), value: dash(text(saved.bankName)) },
                {
                  label: t('계좌번호'),
                  value: dash(text(saved.accountNumber)),
                },
                { label: t('수취인'), value: dash(text(saved.payee)) },
              ]}
            />
          </TwoColumnInfo>
        </Box>

        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>
            {`4. ${t('필수 확인')}`}
          </InfoSectionTitle>
          <ConfirmList>
            {CONFIRM_TEXTS.map(([key, label]) => {
              const checked = saved[key] === true;
              return (
                <li key={key} className={checked ? 'checked' : ''}>
                  <span className="mark" aria-hidden>
                    {checked ? '✓' : '–'}
                  </span>
                  <span>{t(label)}</span>
                </li>
              );
            })}
          </ConfirmList>
        </Box>
      </Box>
    </ContentSlotRoot>
  );
};

export default ExpenseVoucherSlot;
